import { useEffect, useState, type ChangeEvent } from 'react';
import { connect } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { Column, Table, withErrorBoundary } from '@oliasoft-open-source/react-ui-library';
import translations from 'src/internationalisation/translation-map.json';
import { timeAgo } from 'store/entities/well-details/well-details';
import { getRecentEstimates } from 'src/store/entities/projects/projects';
import type { TRootState } from 'src/store/store-types';
import { orderBy } from 'lodash';
import { useNavigate } from 'react-router-dom';
import { useParams } from 'react-router';

type SortDirection = 'asc' | 'desc';
type FilterMap = Record<string, string>;
type SortMap = Record<string, SortDirection>;
type CellValue = string | number | null | undefined;

interface ProjectRow extends Record<string, CellValue> {
  projectName?: CellValue;
  countryName?: CellValue;
  fieldName?: CellValue;
  siteName?: CellValue;
  wellName?: CellValue;
  wellboreName?: CellValue;
  designName?: CellValue;
  accessed?: CellValue;
  updated?: CellValue;
}

const RecentProjectTable = ({
  getRecentEstimates,
  recentEstimates,
}: {
  getRecentEstimates: () => void;
  recentEstimates: ProjectRow[];
}) => {
  const { t } = useTranslation();
  const [filters, setFilters] = useState<FilterMap>({});
  const [sorts, setSorts] = useState<SortMap>({});
  const [rowsPerPage, setRowsPerPage] = useState<number>(10);
  const [selectedPage, setSelectedPage] = useState<number>(1);
  const navigate = useNavigate();
  const { company } = useParams<{ company: string }>();

  useEffect(() => {
    getRecentEstimates();
  }, []);

  const handleOnChangeInput = (ev: ChangeEvent<HTMLInputElement>, identifier: string) => {
    setSelectedPage(1);
    setFilters((prevFilters) => ({
      ...prevFilters,
      [identifier]: ev.target.value,
    }));
  };

  const headerLabels = [
    { label: t(translations.estimate), identifier: 'projectName' },
    { label: t(translations.country), identifier: 'countryName' },
    { label: t(translations.field), identifier: 'fieldName' },
    { label: t(translations.site), identifier: 'siteName' },
    { label: t(translations.well), identifier: 'wellName' },
    { label: t(translations.wellbore), identifier: 'wellboreName' },
    { label: t(translations.design), identifier: 'designName' },
    { label: t(translations.lastAccessed), identifier: 'accessed' },
    { label: t(translations.lastModified), identifier: 'updated' },
    { label: t(translations.user), identifier: 'updatedBy' },
  ];

  const headerRow = headerLabels.map(({ label, identifier }) => ({
    value: label,
    width: 150,
    hasSort: true,
    onSort: () => {
      const sort = sorts[identifier];
      const newSort: SortDirection = sort === 'asc' ? 'desc' : 'asc';
      setSorts({ [identifier]: newSort });
    },
    sort: sorts[identifier] === 'asc' ? 'down' : 'up',
  }));

  const headerFilter = headerLabels.map(({ identifier }) => ({
    key: identifier,
    value: filters[identifier] ?? '',
    type: 'Input',
    placeholder: t(translations.search),
    onChange: (ev: ChangeEvent<HTMLInputElement>) => handleOnChangeInput(ev, identifier),
  }));

  const filterAndSortDataRows = (
    projects: ProjectRow[],
    filters: Record<string, string>,
    sorts: Record<string, SortDirection>,
  ) => {
    const tableRows = projects.map(
      ({
        projectName,
        countryName,
        fieldName,
        siteName,
        wellName,
        wellboreName,
        designName,
        accessed,
        updated,
        updatedBy,
        projectId,
      }) => ({
        projectName,
        countryName,
        fieldName,
        siteName,
        wellName,
        wellboreName,
        designName,
        accessed,
        updated,
        updatedBy,
        projectId,
      }),
    );
    const matchesFilters = (row: ProjectRow) =>
      Object.keys(filters).every((key) => row[key]?.toString().toLowerCase().includes(filters[key]?.toLowerCase()));

    const filteredRows = tableRows.filter(matchesFilters);

    return orderBy(filteredRows, Object.keys(sorts), Object.values(sorts));
  };

  const filteredData = filterAndSortDataRows(recentEstimates, filters, sorts);
  const startIndex = (selectedPage - 1) * rowsPerPage;
  const endIndex = startIndex + rowsPerPage;

  const dataRows = filteredData
    .map((data) => {
      const formattedData: ProjectRow = {
        ...data,
        accessed: data.accessed ? timeAgo(data.accessed) : '',
        updated: data.updated ? timeAgo(data.updated) : '',
      };

      const rowCells = Object.entries(formattedData)
        .filter(([key]) => key !== 'projectId')
        .map(([key, value]) => {
          if (key === 'projectName' && value) {
            return {
              key,
              value: value ?? '',
              type: 'Link',
              onClick: () => {
                navigate(`/company/${company}/project/${data.projectId}/overview`);
              },
            };
          }
          return {
            key,
            value: value ?? '',
          };
        });

      return {
        cells: rowCells,
      };
    })
    .slice(startIndex, endIndex);

  const table = {
    testId: 'recent-projects-table',
    headers: [{ cells: headerRow }, { cells: headerFilter }],
    rows: dataRows,
    footer: {
      pagination: {
        rowCount: filteredData.length,
        selectedPage,
        rowsPerPage: {
          onChange: (evt: ChangeEvent<HTMLInputElement>) => setRowsPerPage(Number(evt.target.value)),
          options: [
            { label: `10 / ${t(translations.page)}`, value: 10 },
            { label: `20 / ${t(translations.page)}`, value: 20 },
            { label: `50 / ${t(translations.page)}`, value: 50 },
          ],
          value: rowsPerPage,
        },
        onSelectPage: setSelectedPage,
        small: true,
      },
    },
  };

  return (
    <Column padding scroll>
      <Table table={table} />
    </Column>
  );
};

const mapStateToProps = ({ entities }: TRootState) => ({
  recentEstimates: entities.projects.recentEstimates,
});

const mapDispatchToProps = {
  getRecentEstimates,
};

const Container = withErrorBoundary(connect(mapStateToProps, mapDispatchToProps)(RecentProjectTable));

export { Container as RecentProjectTable };
